import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { cn, Icon, Skeleton } from "@nexus/ui";
import { formatPreciseDurationMs } from "@/features/events/lib/event-timing";
import type { ScheduledJobKpiStats } from "../lib/scheduled-job-kpis";

interface ScheduledJobsKpiStripProps {
  stats: ScheduledJobKpiStats | undefined;
  /** Avg / p95 of the charted runs — shared language with Events' avg run. */
  avgDurationMs: number | null;
  p95DurationMs: number | null;
  isLoading: boolean;
  /** Previous window still on screen while the next fetch lands. */
  isStale: boolean;
  isFailingFilterActive: boolean;
  isOverdueFilterActive: boolean;
  onFilterFailing: () => void;
  onFilterOverdue: () => void;
}

const LABEL_CLASS = "text-[11px] uppercase tracking-wider text-muted-foreground";
const VALUE_CLASS = "text-lg font-semibold leading-7 tabular-nums";
const ZERO_VALUE_CLASS = "text-lg font-normal leading-7 tabular-nums text-muted-foreground";
const SECONDARY_CLASS = "text-[11px] text-muted-foreground";
const STAT_CLASS = "flex flex-col gap-0.5 p-1.5 text-left";
const STRIP_CLASS = "-mx-1.5 flex shrink-0 flex-wrap items-center gap-x-6 gap-y-3";
const SKELETON_WIDTHS = ["w-16", "w-20", "w-20", "w-20", "w-28", "w-24"];

function Stat({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className={STAT_CLASS}>
      <span className={LABEL_CLASS}>{label}</span>
      <span className="flex items-baseline gap-1.5">{children}</span>
    </div>
  );
}

function formatDuration(ms: number | null): string {
  if (ms == null) return "—";
  return formatPreciseDurationMs(ms);
}

/**
 * Fleet glance above the run chart — same quiet strip as Events / BigQuery.
 * Window stays on the filter bar (BQ pattern); failing / overdue are the
 * clickable drill-ins.
 */
export function ScheduledJobsKpiStrip({
  stats,
  avgDurationMs,
  p95DurationMs,
  isLoading,
  isStale,
  isFailingFilterActive,
  isOverdueFilterActive,
  onFilterFailing,
  onFilterOverdue,
}: ScheduledJobsKpiStripProps) {
  const { t, i18n } = useTranslation();
  const locale = i18n.language;

  if (isLoading || !stats) {
    return (
      <div className={STRIP_CLASS}>
        {SKELETON_WIDTHS.map((width, index) => (
          <Skeleton
            key={index}
            className={cn(
              "h-11 rounded-sm bg-foreground/8",
              width,
              index === SKELETON_WIDTHS.length - 1 && "ml-auto",
            )}
          />
        ))}
      </div>
    );
  }

  const hasFailing = stats.failing > 0;
  const hasOverdue = stats.overdue > 0;
  const canFilterFailing = hasFailing || isFailingFilterActive;
  const canFilterOverdue = hasOverdue || isOverdueFilterActive;
  const isBusy = stats.inFlight > 0;

  return (
    <div
      className={cn(
        STRIP_CLASS,
        "[&>*]:transition-opacity [&>*]:duration-200",
        isStale && "[&>*]:opacity-40",
      )}
      aria-busy={isStale || undefined}
    >
      <Stat label={t("admin.scheduledJobs.kpi.jobs", "Jobs")}>
        <span className={stats.jobs > 0 ? VALUE_CLASS : ZERO_VALUE_CLASS}>
          {stats.jobs.toLocaleString(locale)}
        </span>
      </Stat>

      <Stat label={t("admin.scheduledJobs.kpi.enabled", "Enabled")}>
        <span className={stats.enabled > 0 ? VALUE_CLASS : ZERO_VALUE_CLASS}>
          {stats.enabled.toLocaleString(locale)}
        </span>
      </Stat>

      <button
        type="button"
        onClick={onFilterFailing}
        disabled={!canFilterFailing}
        aria-pressed={isFailingFilterActive}
        title={
          canFilterFailing
            ? t("admin.scheduledJobs.kpi.failingHint", "Filter to failing jobs")
            : t("admin.scheduledJobs.kpi.failingNone", "No failing jobs")
        }
        className={cn(
          STAT_CLASS,
          "rounded-sm transition-colors",
          canFilterFailing ? "cursor-pointer hover:bg-foreground/5" : "cursor-default",
          isFailingFilterActive && "bg-error-subtle hover:bg-error-subtle",
        )}
      >
        <span className={LABEL_CLASS}>{t("admin.scheduledJobs.kpi.failing", "Failing")}</span>
        <span className="flex items-baseline gap-1.5">
          <span
            className={cn(
              hasFailing ? VALUE_CLASS : ZERO_VALUE_CLASS,
              hasFailing && "text-error",
            )}
          >
            {stats.failing.toLocaleString(locale)}
          </span>
        </span>
      </button>

      <button
        type="button"
        onClick={onFilterOverdue}
        disabled={!canFilterOverdue}
        aria-pressed={isOverdueFilterActive}
        title={
          canFilterOverdue
            ? t("admin.scheduledJobs.kpi.overdueHint", "Filter to overdue jobs")
            : t("admin.scheduledJobs.kpi.overdueNone", "No overdue jobs")
        }
        className={cn(
          STAT_CLASS,
          "rounded-sm transition-colors",
          canFilterOverdue ? "cursor-pointer hover:bg-foreground/5" : "cursor-default",
          isOverdueFilterActive && "bg-warning-subtle hover:bg-warning-subtle",
        )}
      >
        <span className={LABEL_CLASS}>{t("admin.scheduledJobs.kpi.overdue", "Overdue")}</span>
        <span className="flex items-baseline gap-1.5">
          <span
            className={cn(
              hasOverdue ? VALUE_CLASS : ZERO_VALUE_CLASS,
              hasOverdue && "text-warning",
            )}
          >
            {stats.overdue.toLocaleString(locale)}
          </span>
        </span>
      </button>

      <Stat label={t("admin.scheduledJobs.kpi.avgRun", "Avg run")}>
        <span className={avgDurationMs == null ? ZERO_VALUE_CLASS : VALUE_CLASS}>
          {formatDuration(avgDurationMs)}
        </span>
        {p95DurationMs != null ? (
          <span className={SECONDARY_CLASS}>
            {t("admin.scheduledJobs.kpi.p95Run", "p95 {{duration}}", {
              duration: formatDuration(p95DurationMs),
            })}
          </span>
        ) : null}
      </Stat>

      <div className="ml-auto">
        <Stat label={t("admin.scheduledJobs.kpi.inFlight", "In flight")}>
          <span className={isBusy ? VALUE_CLASS : ZERO_VALUE_CLASS}>
            {stats.inFlight.toLocaleString(locale)}
          </span>
          {isBusy ? (
            <Icon
              name="loader"
              size="xs"
              colorVar="--info"
              className="animate-spin motion-reduce:animate-none"
              aria-hidden
            />
          ) : null}
        </Stat>
      </div>
    </div>
  );
}
